import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import {
  MoreVertical,
  Eye,
  Download,
  Edit,
  Info,
  Trash2,
  Paperclip,
  Check,
  FileCode,
  RefreshCw,
  Sparkles
} from 'lucide-react';

export interface DocumentAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  className?: string;
  disabled?: boolean;
  hidden?: boolean;
}

interface DocumentActionsDropdownProps {
  actions: DocumentAction[];
  isAttached?: boolean;
  onAttachToggle?: () => void;
  showAttachButton?: boolean;
  className?: string;
  buttonClassName?: string;
  dropdownClassName?: string;
  position?: 'left' | 'right';
}

const DocumentActionsDropdown: React.FC<DocumentActionsDropdownProps> = ({
  actions,
  isAttached = false,
  onAttachToggle,
  showAttachButton = true,
  className = '',
  buttonClassName = '',
  dropdownClassName = '',
  position = 'right'
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Compute position of dropdown relative to the viewport
  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const menuWidth = 224; // w-56 = 14rem = 224px
    const menuHeight = 300; // rough max height estimate
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow < menuHeight ? rect.top - menuHeight : rect.bottom + 4;
    const left = position === 'left'
      ? rect.right - menuWidth
      : rect.left;
    setDropdownPos({ top, left: Math.max(4, left) });
  }, [position]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      if (event.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Recalculate position on scroll/resize while open
  useEffect(() => {
    if (!isOpen) return;
    const handleReposition = () => updatePosition();
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);
    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [isOpen, updatePosition]);

  const visibleActions = actions.filter(action => !action.hidden);
  
  // Separate delete action if it exists
  const deleteAction = visibleActions.find(action => action.id === 'delete');
  const otherActions = visibleActions.filter(action => action.id !== 'delete');

  return (
    <div className={`relative inline-flex items-center gap-2 ${className}`}>
      {/* Attach/Detach Button */}
      {showAttachButton && onAttachToggle && (
        <button
          onClick={onAttachToggle}
          className={`
            p-2 rounded-lg transition-all duration-200
            ${isAttached 
              ? 'bg-primary-100 text-primary-600 hover:bg-primary-200' 
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }
          `}
          title={isAttached ? 'Rimuovi dal contesto' : 'Aggiungi al contesto'}
        >
          {isAttached ? (
            <Check className="h-5 w-5" />
          ) : (
            <Paperclip className="h-5 w-5" />
          )}
        </button>
      )}

      {/* Dropdown Button */}
      <button
        ref={buttonRef}
        onClick={() => {
          if (!isOpen) updatePosition();
          setIsOpen(!isOpen);
        }}
        className={`
          p-2 rounded-lg transition-all duration-200
          text-gray-500 hover:text-gray-700 hover:bg-gray-100
          focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1
          ${buttonClassName}
        `}
        aria-label={t('common.moreActions')}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <MoreVertical className="h-5 w-5" />
      </button>

      {/* Dropdown Menu - rendered via portal to avoid overflow clipping */}
      {isOpen && dropdownPos && createPortal(
        <div
          ref={dropdownRef}
          className={`
            fixed w-56 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5
            transform transition-all duration-200 ease-out
            ${dropdownClassName}
          `}
          style={{ top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999 }}
        >
          <div className="py-1" role="menu" aria-orientation="vertical">
            {/* Regular Actions */}
            {otherActions.map((action, index) => (
              <button
                key={action.id}
                onClick={() => {
                  if (!action.disabled) {
                    action.onClick();
                    setIsOpen(false);
                  }
                }}
                disabled={action.disabled}
                className={`
                  w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left
                  transition-colors duration-150
                  ${action.disabled 
                    ? 'text-gray-400 cursor-not-allowed' 
                    : 'text-gray-700 hover:bg-gray-50'
                  }
                  ${action.className || ''}
                `}
                role="menuitem"
              >
                <span className="flex-shrink-0 text-gray-500">
                  {action.icon}
                </span>
                <span className="flex-1">{action.label}</span>
              </button>
            ))}

            {/* Separator before delete */}
            {deleteAction && otherActions.length > 0 && (
              <div className="my-1 border-t border-gray-100" />
            )}

            {/* Delete Action */}
            {deleteAction && (
              <button
                onClick={() => {
                  if (!deleteAction.disabled) {
                    deleteAction.onClick();
                    setIsOpen(false);
                  }
                }}
                disabled={deleteAction.disabled}
                className={`
                  w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left
                  transition-colors duration-150
                  ${deleteAction.disabled 
                    ? 'text-gray-400 cursor-not-allowed' 
                    : 'text-red-600 hover:bg-red-50'
                  }
                `}
                role="menuitem"
              >
                <span className="flex-shrink-0">
                  <Trash2 className="h-4 w-4" />
                </span>
                <span className="flex-1">{deleteAction.label}</span>
              </button>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// Export common action creators for consistency
export const createCommonActions = {
  viewPdf: (onClick: () => void): DocumentAction => ({
    id: 'view-pdf',
    label: i18n.t('documents.list.viewPdf'),
    icon: <Eye className="h-4 w-4" />,
    onClick
  }),

  viewText: (onClick: () => void): DocumentAction => ({
    id: 'view-text',
    label: i18n.t('documentActions.viewText'),
    icon: <FileCode className="h-4 w-4" />,
    onClick
  }),

  download: (onClick: () => void): DocumentAction => ({
    id: 'download',
    label: i18n.t('documentActions.downloadOriginal'),
    icon: <Download className="h-4 w-4" />,
    onClick
  }),

  edit: (onClick: () => void): DocumentAction => ({
    id: 'edit',
    label: i18n.t('common.edit'),
    icon: <Edit className="h-4 w-4" />,
    onClick
  }),

  info: (onClick: () => void): DocumentAction => ({
    id: 'info',
    label: i18n.t('documentActions.info'),
    icon: <Info className="h-4 w-4" />,
    onClick
  }),

  reprocess: (onClick: () => void): DocumentAction => ({
    id: 'reprocess',
    label: i18n.t('documents.list.reprocess'),
    icon: <RefreshCw className="h-4 w-4" />,
    onClick,
    className: 'text-blue-600 hover:bg-blue-50',
  }),

  reprocessAI: (onClick: () => void): DocumentAction => ({
    id: 'reprocessAI',
    label: i18n.t('documentActions.reprocessAI'),
    icon: <Sparkles className="h-4 w-4" />,
    onClick,
    className: 'text-purple-600 hover:bg-purple-50',
  }),

  delete: (onClick: () => void): DocumentAction => ({
    id: 'delete',
    label: i18n.t('common.delete'),
    icon: <Trash2 className="h-4 w-4" />,
    onClick
  })
};

export { DocumentActionsDropdown };
export default DocumentActionsDropdown;